import {keysOf} from '@peachy/utility-kit-pure'

// applies to Plans and PlanBenefits
const BasicAlterationTypes = [
    'ADDED',
    'REMOVED',
    'ALTERED'
] as const


// applies to everything except Plans and PlanBenefits
const LifecycleAlterationTypes = [
    'ADDED',
    'ALTERED',
    'RENEWED',
    'TRANSFERRED-IN',
    'TRANSFERRED-OUT',
    'CANCELLED',
    'REACTIVATED',
] as const


// applies to PlanBenefits and LifeBenefits
export const BenefitPropertyAlterations = [
    'LIMIT-INCREASED',
    'LIMIT-DECREASED',
    'EXCESS-INCREASED',
    'EXCESS-DECREASED',
] as const

// applies to Lives only
const LifePropertyAlterations = [
    'ADDRESS-UPDATED',
    'BIRTHDATE-UPDATED',
    'PLAN-SWITCHED',
] as const


// exported alterations

export const LifeBenefitAlterations = [
    ...prefixWith('LIFE-BENEFIT', [
        ...LifecycleAlterationTypes,
        ...BenefitPropertyAlterations
    ] as const),
] as const


export const LifeAlterations = [
    ...prefixWith('LIFE', [
        ...LifecycleAlterationTypes,
        ...LifePropertyAlterations,
    ] as const),
] as const

export const PolicyAlterations = [
    ...prefixWith('POLICY', [
        ...LifecycleAlterationTypes,
        ...prefixWith('PLAN', [
            ...BasicAlterationTypes,
            ...prefixWith('BENEFIT', [
                ...BasicAlterationTypes,
                ...BenefitPropertyAlterations
            ] as const)
        ] as const)
    ] as const),
] as const


export const SubscriptionAlterations = [
    ...prefixWith('SUBSCRIPTION', [
        ...LifecycleAlterationTypes,
        ...prefixWith('PLAN', [
            ...BasicAlterationTypes,
            ...prefixWith('BENEFIT', [
                ...BasicAlterationTypes,
                ...BenefitPropertyAlterations
            ] as const)
        ] as const)
    ] as const),
] as const

export const AccountAlterations = [
    ...prefixWith('ACCOUNT', [
        ...LifecycleAlterationTypes,
    ] as const),
] as const

export const AllAlterations = [
    ...AccountAlterations,
    ...SubscriptionAlterations,
    ...PolicyAlterations,
    ...LifeAlterations,
    ...LifeBenefitAlterations,
]


// alteration types

export type AccountAlterationType = typeof AccountAlterations[number]
export type SubscriptionAlterationType = typeof SubscriptionAlterations[number]
export type PolicyAlterationType = typeof PolicyAlterations[number]
export type LifeAlterationType = typeof LifeAlterations[number]
export type LifeBenefitAlterationType = typeof LifeBenefitAlterations[number]


export type AlterationType =
    | AccountAlterationType
    | SubscriptionAlterationType
    | PolicyAlterationType
    | LifeAlterationType
    | LifeBenefitAlterationType


// alteration namespaces


export const AvailableBenefitAlteration = keysOf<AccountAlterationType>()

export const AvailableAccountAlteration = keysOf<AccountAlterationType>()
export const AvailableSubscriptionAlteration = keysOf<SubscriptionAlterationType>()
export const AvailablePolicyAlteration = keysOf<PolicyAlterationType>()
export const AvailableLifeAlteration = keysOf<LifeAlterationType>()
export const AvailableLifeBenefitAlteration = keysOf<LifeBenefitAlterationType>()
export const AvailableAlteration = keysOf<AlterationType>()



// helpers
export function prefixWith<const Prefix extends string, const Alterations extends readonly string[]>(
    prefix: Prefix,
    alterations: Alterations
): PrefixedAlteration<Prefix, Alterations[number]>[] {
    return alterations.map(alt => `${prefix}-${alt}` as PrefixedAlteration<Prefix, Alterations[number]>)
}

type PrefixedAlteration<Prefix extends string, Alteration extends string> = `${Prefix}-${Alteration}`


export type AlterationAttribution = {
    actor: string,
    effectiveDate: number
    description: string
}


export type Alteration = AlterationAttribution & {
    id: string
    path: string[]
    type: AlterationType
    versionIdx: number
    from?: unknown
    to?: unknown
}


export function hasAlteration(alterationType: AlterationType, alterations: Alteration[]) {
    return alterations.some(a => a.type = alterationType)
}
